/* eslint-disable prefer-destructuring */
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormGroup,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import {
  CaseNote,
  EconsultProviderTypes,
  EconsultUserRoles,
  NoteActionTypes,
  NoteParticipantStatusReasons,
  NoteStatusCodes,
  NoteTypes
} from '@oh-vcp/components-common';
import { CaseDetails, Kpi } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckBoxOptionControl,
  EconsultTextField,
  useNotifierContext,
  RequiredFieldTypographyWithInfo
} from '@oh-vcp/components-ui';
import { useQueryClient } from 'react-query';
import CaseService from '../services/caseService';
import { useAuth } from '../auth/AuthContext';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultSendDialog from '../econsultSendDialog/EconsultSendDialog';
import EconsultDraftLostWarningDialog from '../econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}));
interface EconsultCaseReturnConsultProps {
  reasonForReturning: string;
  onReasonChanged: (reason: string) => void;
  draftNote?: CaseNote;
  onNoteContentChanged?: (noteContent: string) => void;
  isDraftChanged: () => boolean | false;
  setPreviousDraftNote: (prevDraftNote: CaseNote | undefined) => void;
}

const ReturnButton = styled(Button)({
  width: 100,
  marginTop: 10,
  marginBottom: 10
});

const EconsultCaseReturnConsult: FC<EconsultCaseReturnConsultProps> = ({
  reasonForReturning,
  onReasonChanged,
  draftNote,
  onNoteContentChanged,
  isDraftChanged,
  setPreviousDraftNote
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { providerType, statusType } = useParams();
  const queryClient = useQueryClient();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const {
    caseDetails,
    setCaseDetails,
    setDraftNote,
    userRole,
    setDraftCaseSaveDisable,
    setDraftCaseSaved,
    setReturnNoteSaved,
    abortAllUploads
  } = useEconsultCaseDetailsContext();
  const [noteContent, setNoteContent] = useState<string>(draftNote?.noteContent || '');
  const [isSendDialogOpen, setIsSendDialogOpen] = useState<boolean>(false);
  const [isReturnButtonDisabled, setIsReturnButtonDisabled] = useState<boolean>(false);
  const [isReturnError, setIsReturnError] = useState<boolean>(false);
  const isInitialized = useRef(false);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const [draftNoteChanged, setDraftNoteChanged] = useState<boolean>(false);

  if (!user || !caseDetails) {
    return null;
  }
  const handleBeforeUnload = (event: any) => {
    if (window.location.toString().indexOf('logout') > -1) {
      return;
    }
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome and Firefox
    return ''; // Required for Safari
  };
  const handlePopState = (event: PopStateEvent) => {
    console.log(`popstate: ${event}`);
    if (caseDetails && isDraftChanged()) {
      setIsDraftWarningDialogOpen(true);
    }
  };
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isDraftChanged() || draftNoteChanged) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isDraftChanged, noteContent, draftNoteChanged]);
  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const getDraftReturnNote = (reason: string | undefined) => {
    if (!reason) {
      reason = reasonForReturning;
    }
    const actType =
      reason === 'notAvailable' &&
      !CaseService.hasSpecialistResponded(caseDetails) &&
      caseDetails?.participant?.program
        ? NoteActionTypes.unassign
        : NoteActionTypes.decline;
    const retNote: CaseNote = {
      ownerId: user.userId as number,
      noteTypeCd: NoteTypes.consultant,
      actionTypeCd: actType,
      caseId: caseDetails.caseId,
      statusCd: NoteStatusCodes.draft,
      kpis: undefined,
      parameter1: t(`Case.consult.${reasonForReturning}`) as string,
      noteContent
    };
    return retNote;
  };

  const getCaseDraftNotes = async (caseId: number | undefined) => {
    if (!caseId) {
      return undefined;
    }
    const caseInfo = await CaseService.getCaseDetails(caseId);
    const noteType =
      userRole === EconsultProviderTypes.consultant.toUpperCase() ||
      userRole === EconsultUserRoles.consultantDelegate
        ? NoteTypes.consultant
        : NoteTypes.referrer;
    const draftNotes: CaseNote[] = [];
    if (caseInfo && caseInfo.data && caseInfo.data.notes) {
      caseInfo.data.notes.forEach((note) => {
        if (
          note.statusCd &&
          note.statusCd === NoteStatusCodes.draft &&
          noteType === note.noteTypeCd
        ) {
          draftNotes.push(note);
        }
      });
    }
    return draftNotes;
  };

  const handleDraftSaved = async () => {
    const caseDraftNotes = await getCaseDraftNotes(caseDetails?.caseId);
    const noteId =
      caseDraftNotes && caseDraftNotes.length > 0 ? caseDraftNotes[0].noteId : undefined;
    const draftNoteToUpsert = getDraftReturnNote('');
    if (!draftNoteToUpsert.noteId && noteId) {
      draftNoteToUpsert.noteId = noteId;
    }
    const upsertedNote = await CaseService.upsertCaseNote(draftNoteToUpsert);
    setCaseDetails({
      ...caseDetails,
      notes: [...(caseDetails?.notes || []), upsertedNote]
    });
    setReturnNoteSaved(true);
    setDraftCaseSaveDisable(false);
    setDraftCaseSaved(true);
    setDraftNoteChanged(false);
    setPreviousDraftNote(JSON.parse(JSON.stringify(draftNoteToUpsert)));
  };

  useEffect(() => {
    if (!isInitialized.current && caseDetails) {
      handleDraftSaved();
      isInitialized.current = true;
    }
  }, [caseDetails]);

  const handleReasonForReturning = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reason = event.target.name;
    if (reason) {
      setIsReturnError(false);
    }
    onReasonChanged(reason);
    setDraftNote(getDraftReturnNote(reason));
  };
  const getReturnActionType = () => {
    return reasonForReturning === 'notAvailable' &&
      !CaseService.hasSpecialistResponded(caseDetails) &&
      caseDetails?.participant?.program
      ? NoteActionTypes.unassign
      : NoteActionTypes.decline;
  };

  const handleNoteContentChanged = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNoteContent(event.target.value);
    setDraftNote(getDraftReturnNote(undefined));
    setDraftNoteChanged(true);
    if (onNoteContentChanged) {
      onNoteContentChanged(event.target.value);
    }
    setReturnNoteSaved(false);
  };
  const getRejectedReason = () => {
    switch (reasonForReturning) {
      case 'incorrectSpecialty':
        return NoteParticipantStatusReasons.incorrectSpecialty;
      case 'notAvailable':
        return NoteParticipantStatusReasons.consultantUnavailable;
      default:
        return NoteParticipantStatusReasons.rejectedOther;
    }
  };

  const handleConfirm = async (kpi?: Kpi) => {
    setIsReturnButtonDisabled(true);
    let noteToReturn: CaseNote | undefined;
    const caseDraftNotes = await getCaseDraftNotes(caseDetails?.caseId);
    if (caseDraftNotes && caseDraftNotes.length > 0) {
      noteToReturn = caseDraftNotes[0];
      noteToReturn.parameter1 = t(`Case.consult.${reasonForReturning}`) as string;
      noteToReturn.statusCd = NoteStatusCodes.active;
      noteToReturn.actionTypeCd = getReturnActionType();
      noteToReturn.noteContent = noteContent;
    } else {
      noteToReturn = {
        ownerId: user.userId as number,
        noteTypeCd: NoteTypes.consultant,
        actionTypeCd: getReturnActionType(),
        caseId: caseDetails.caseId,
        statusCd: NoteStatusCodes.active,
        parameter1: t(`Case.consult.${reasonForReturning}`) as string,
        noteContent
      };
    }
    const returnedNote = await CaseService.upsertCaseNote(noteToReturn);
    const updatedCaseDetails: CaseDetails = {
      ...caseDetails,
      participant: {
        ...caseDetails?.participant,
        statusCd:
          reasonForReturning === 'notAvailable' ? NoteStatusCodes.active : NoteStatusCodes.closed,
        statusReason: getRejectedReason(),
        ...(reasonForReturning === 'notAvailable' && { triageStatusCd: NoteStatusCodes.active })
      },
      notes: [
        {
          ...returnedNote,
          kpis: kpi ? [kpi] : undefined
        }
      ]
    };
    setCaseDetails(updatedCaseDetails);
    removeAllErrorNotifications();
    await CaseService.upsertCaseDetails(updatedCaseDetails);
    setIsSendDialogOpen(false);
    addNotification({
      type: 'success',
      message: t('EconsultCaseReturned.success.message')
    });
    setIsReturnButtonDisabled(false);
    setTimeout(() => {
      if (providerType === 'search') {
        queryClient.invalidateQueries(['caseSearch']);
      } else {
        queryClient.invalidateQueries(['caseResults']);
      }
    }, 300);
    const urlToNavigate = CaseService.getReturnNavigationUrl(providerType, statusType, user);
    navigate(urlToNavigate.toLowerCase());
  };
  const handleCancel = () => {
    setIsReturnButtonDisabled(false);
    setIsSendDialogOpen(false);
  };
  const handleReturn = async () => {
    if (!reasonForReturning) {
      setIsReturnError(true);
      return;
    }
    setIsReturnError(false);
    setIsReturnButtonDisabled(true);
    if (reasonForReturning === 'notAvailable') {
      await handleConfirm();
    } else {
      setIsSendDialogOpen(true);
    }
  };
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setDraftNote(undefined);
    setCaseDetails(undefined);
    abortAllUploads();
    navigate(-1); // Allow navigation back
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
    window.history.pushState(null, '', window.location.href);
  };
  return (
    <>
      <Stack direction="column">
        <HtmlTooltip
          placement="top-start"
          title={<>{t('Case.consult.reasonForReturning.tool.tip')}</>}>
          <RequiredFieldTypographyWithInfo variant="body2">
            {t('Case.consult.reasonForReturning')}
          </RequiredFieldTypographyWithInfo>
        </HtmlTooltip>
        <FormGroup>
          <Box display="flex" alignItems="center">
            <CheckBoxOptionControl
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  onChange={handleReasonForReturning}
                  checked={reasonForReturning === 'incorrectSpecialty'}
                  name="incorrectSpecialty"
                />
              }
              label=""
              disableTypography
            />
            <Box component="span" sx={{ ml: 0, fontSize: '14px' }}>
              {t('Case.consult.incorrectSpecialty')}
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <CheckBoxOptionControl
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  onChange={handleReasonForReturning}
                  checked={reasonForReturning === 'notAvailable'}
                  name="notAvailable"
                />
              }
              label=""
              disableTypography
            />
            <Box component="span" sx={{ ml: 0, fontSize: '14px' }}>
              {t('Case.consult.notAvailable')}
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <CheckBoxOptionControl
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  onChange={handleReasonForReturning}
                  checked={reasonForReturning === 'other'}
                  name="other"
                />
              }
              label=""
              disableTypography
            />
            <Box component="span" sx={{ ml: 0, fontSize: '14px' }}>
              {t('Case.consult.other')}
            </Box>
          </Box>
        </FormGroup>
        {isReturnError && (
          <Typography variant="body2" color="red">
            {t('Case.consult.requiredField')}
          </Typography>
        )}
        <EconsultTextField
          value={noteContent}
          onChange={handleNoteContentChanged}
          multiline
          rows={2}
          fullWidth
          placeholder={`${t('Case.consult.enterComments')}${
            reasonForReturning === 'other' ? '' : ` ${t('Case.consult.optional')}`
          } - ${t('Case.consult.doNotIncludePersonalDetails')}`}
          inputProps={{
            style: {
              resize: 'vertical',
              overflow: 'auto'
            }
          }}
        />
        <ReturnButton
          variant="contained"
          color="primary"
          onClick={handleReturn}
          disabled={isReturnButtonDisabled}>
          {t('Case.consult.return')}
        </ReturnButton>
      </Stack>
      <EconsultSendDialog
        isOpen={isSendDialogOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
    </>
  );
};

export default EconsultCaseReturnConsult;
